import { queryOptions, type UseQueryOptions } from '@tanstack/react-query';

import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { STANDARD_STALE_TIME } from '$/services/constants';
import {
  getAllProjects,
  getEditorProject,
  getEditorProjectHarmoniesWithAi,
  getInspirationProject,
  getLegacyInspirationProject,
  getProjectFolders,
} from '$/services/usecases/projects';
import { type EditorProject } from '$/services/usecases/projects/mapper/editorProject';

export const projectsQuery = (
  { enabled }: { enabled: boolean } = { enabled: true },
) =>
  queryOptions({
    queryKey: ['projects', useAuthenticationStore.getState().user?.id, enabled],
    queryFn: () => getAllProjects(),
    staleTime: STANDARD_STALE_TIME,
    enabled,
  });

export const projectFoldersQuery = (
  { enabled }: { enabled: boolean } = { enabled: true },
) =>
  queryOptions({
    queryKey: ['projectFolders', useAuthenticationStore.getState().user?.id],
    queryFn: () => getProjectFolders(),
    staleTime: STANDARD_STALE_TIME,
    enabled,
  });

export const inspirationProjectQuery = (projectId: string, enabled = true) =>
  queryOptions({
    queryKey: [
      'project-inspiration',
      useAuthenticationStore.getState().user?.id,
      projectId,
    ],
    enabled,
    queryFn: () => getInspirationProject(projectId),
    staleTime: 0,
  });

export const editorProjectQuery = (
  projectId: string | undefined,
  options: Omit<UseQueryOptions<EditorProject | null, Error>, 'queryKey'> = {},
) =>
  queryOptions({
    initialData: null,
    queryKey: [
      'project-editor',
      useAuthenticationStore.getState().user?.id,
      projectId,
    ],
    enabled: !!projectId,
    queryFn: async () => {
      if (!projectId) return null;
      const response = await getEditorProject(projectId);
      return response.response.payload ?? null;
    },
    ...options,
  });

export const editorProjectAiHarmoniesQuery = (projectId: string | undefined) =>
  queryOptions({
    queryKey: [
      'project-editor-ai-harmonies',
      useAuthenticationStore.getState().user?.id,
      projectId,
    ],
    enabled: !!projectId,
    queryFn: async () => {
      if (!projectId) throw new Error('Project is not loaded');

      const response = await getEditorProjectHarmoniesWithAi(projectId);

      if (!response.isSuccessful || !response.response.payload)
        throw new Error('Failed to load harmonies');

      return response.response.payload;
    },
    staleTime: STANDARD_STALE_TIME,
  });

export const legacyInspirationProjectQuery = (
  projectId: string,
  enabled = true,
) =>
  queryOptions({
    queryKey: ['project-legacy-inspiration', projectId],
    enabled,
    queryFn: () => getLegacyInspirationProject(projectId),
    staleTime: Infinity, // legacy readonly inspirations will never change
  });
